import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay';
import { withRouter } from 'react-router-dom';
import styled from 'react-emotion';
import { css } from 'emotion';
import i18next from 'i18next';
import withMedia from '~/modules/core/utils/mediaHelpers/withMedia';

import Spacer from '~/modules/coreUI/components/layouts/helpers/Spacer';
import withRootQuery from '~/modules/core/utils/relayHelpers/withRootQuery';
import BannerImage from '~/modules/ecommerceCoreUI/components/basic/BannerImage';
import { cssMediaMax } from '~/modules/core/utils/cssHelpers/cssMedia';
import { Column } from '~/modules/coreUI/components/layouts/helpers/LinearLayout';
import { Label } from '~/modules/coreUI/components/basic/Labels';
import Rating from '~/modules/ecommerceCoreUI/components/basic/Rating';
import Follow from '~/modules/ecommerceCoreUI/components/productDetails/vendor/buttons/Follow';
import ShareButtons from '~/modules/ecommerceCoreUI/components/ShareButtons/ShareButtons';
import Icon from '~/modules/coreUI/components/basic/Icon';
import { scrollToContainerTop, scrollToContainerTopById } from '~/modules/core/utils/jsHelpers/ScrollToTop';

import ShopDetailsHeaderDesktop, {
  ShopImage,
  ratingCountStyle,
  shareButtonsStyle,
  shareLabelStyle,
} from './ShopDetailsHeaderDesktop';
import ShopDetailsHeaderResponsive from './ShopDetailsHeaderResponsive';
import Location from '../../components/ShopDetails/Location';
import Telephone from '../../components/ShopDetails/Telephone';

const customStyle = () => css`
  height: 300px;
  ${cssMediaMax.tablet`
    height: 200px;
  `} ${cssMediaMax.mobile`
    height: 120px!important;
  `};
`;

const ratingStyle = props => css`
  ${cssMediaMax.tablet`
     margin-left: ${props.theme.paddings.normal}px;
     font-size: ${props.theme.fonts.sizes.large}px !important;
  `} ${cssMediaMax.mobile`
      font-size: ${props.theme.fonts.sizes.medium}px !important;
  `};
`;

export const ShopNameTitle = styled.span`
  color: ${props => props.theme.colors.labels.important};
  font-size: ${props => props.theme.fonts.sizes.xxLarge}px;
  font-weight: bold;
  text-transform: capitalize;
  ${props => cssMediaMax.tablet`
    font-size: ${props.theme.fonts.sizes.xLarge}px;
  `} ${props => cssMediaMax.mobile`
    font-size: ${props.theme.fonts.sizes.large}px;
  `};
`;

const ShopDetailsHeader = ({ queryResult, media }) => {
  const Layout = media.minTablet ? ShopDetailsHeaderDesktop : ShopDetailsHeaderResponsive;

  const phoneNumbers = () => {
    const entries = [];
    if (queryResult.shop.branches[0].first_phone_number) {
      const firstNumber = {
        code: queryResult.shop.branches[0].first_phone_number_code,
        number: queryResult.shop.branches[0].first_phone_number,
      };
      entries.push(firstNumber);
    }
    if (queryResult.shop.branches[0].second_phone_number) {
      const secondNumber = {
        code: queryResult.shop.branches[0].second_phone_number_code,
        number: queryResult.shop.branches[0].second_phone_number,
      };
      entries.push(secondNumber);
    }
    if (queryResult.shop.branches[0].landline) {
      const lineNumber = {
        code: queryResult.shop.branches[0].landline_code,
        number: queryResult.shop.branches[0].landline,
      };
      entries.push(lineNumber);
    }
    return entries;
  };

  return (
    queryResult
    && queryResult.shop && (
      <React.Fragment>
        <Spacer size={2} />
        <BannerImage
          customStyle={customStyle}
          imgSrc={queryResult.shop.banner_url}
          imgName={queryResult.shop.name}
          isRounded={false}
        />
        <Layout
          ShopLogo={<ShopImage src={queryResult.shop.logo_url} alt={queryResult.shop.name} />}
          shopName={(
            <React.Fragment>
              <Spacer size={2} />
              <Label important semiBold size={['lg', 'lg', 'xxl', 'xxl']}>
                {queryResult.shop.name}
              </Label>
            </React.Fragment>
           )}
          Rating={(
            <div onClick={() => { scrollToContainerTopById('reviewForm'); }}> {/* eslint-disable-line */}
              <Rating
                rating={queryResult.shop.avg_rating}
                ratingCount={queryResult.shop.rating_count}
                starsSize={27}
                customCountStyles={ratingCountStyle}
                customStyle={ratingStyle}
              />
            </div>
          )}
          ShopAddress={
            queryResult.shop.branches.length > 0 ? (
              <Column fullWidth>
                <Telephone entries={phoneNumbers()} />
                <Spacer size={1} />
                <Location
                  address={queryResult.shop.branches[0].address}
                  lat={queryResult.shop.branches[0].lat}
                  lng={queryResult.shop.branches[0].lng}
                />
              </Column>
            ) : null
          }
          actionButtons={(
            <React.Fragment>
              <Follow size={['sm']} followed={queryResult.shop.follow} vendorId={queryResult.shop.ref_id} />
              <Spacer size={1} />
              <ShareButtons
                vertical
                label={i18next.t('eCommerceShops:ShopDetails.shareTitle')}
                customIconsStyle={shareButtonsStyle}
                customLabelStyle={shareLabelStyle}
              />
            </React.Fragment>
         )}
          responsiveActionButtons={(
            <React.Fragment>
              <Follow tight size={['xs']} followed={queryResult.shop.follow} vendorId={queryResult.shop.ref_id} />
              <ShareButtons vertical customIconsStyle={shareButtonsStyle} />
            </React.Fragment>
         )}
          shopInfoIcon={(
            <Icon onClick={() => scrollToContainerTop('shopInfoSection')} secondary size={[2, 2, 3, 3]} className="el-icon el-icon-info-1" />
         )}
        />
      </React.Fragment>
    )
  );
};

ShopDetailsHeader.propTypes = {
  queryResult: PropTypes.shape({}).isRequired,
  vendorID: PropTypes.string.isRequired,
};

export default withRouter(
  withRootQuery(
    withMedia(ShopDetailsHeader),
    graphql`
      query ShopDetailsHeaderQuery($vendorID: ID!) {
        shop(id: $vendorID) {
          id
          ref_id
          name
          avg_rating
          banner_url
          follow
          logo_url
          rating_count
          branches {
            address
            city
            country
            headquarter
            first_phone_number
            first_phone_number_code
            id
            second_phone_number
            second_phone_number_code
            lat
            lng
            inwani_building
            inwani_street
            inwani_zone
            landline
            landline_code
          }
        }
      }
    `,
    null,
    props => ({
      vendorID: props.vendorID,
    }),
  ),
);
